import { FC } from 'react';

import { apiOrganizationSaveVerifyAccount } from 'src/api/organization';
import { UiRadioGroupPropsValue } from 'src/components/UI/radioGroup/types';
import { UiToast } from 'src/components/UI/toasts';
import { Enums } from 'src/models/enums';
import {
  OrganizationOldVerifyPageModelType,
  OrganizationOldVerifyPageSaveRequestType,
} from 'src/models/Organization/OldVerify';
import { useBeforeunloadOrganizationClean } from 'src/pages/Organization/common/hooks/useBeforeunloadOrganization';
import { OrganizationLayout } from 'src/pages/Organization/common/Layout';
import { OrganizationOldVerifyInPendingVerificationPartial } from 'src/pages/Organization/OldVerify/partials/InPendingVerification';
import { OrganizationOldVerifyNotVerificationCardPartial } from 'src/pages/Organization/OldVerify/partials/NotVerificationCard';
import { OrganizationOldVerifyVerificationCardPartial } from 'src/pages/Organization/OldVerify/partials/VerificationCard';
import { ROUTE_START_GUIDE_ORGANIZATION } from 'src/routes';
import { hardRedirect } from 'src/utils/hardRedirect';
import { useOneRequest } from 'src/utils/hooks/useOneRequest';
import { useNsTHelper } from 'src/utils/i18n';
import { useStateForm } from 'src/utils/stateForm';
import { useStateFormValueWatch } from 'src/utils/stateForm/useFormWatch/useStateFormValueWatch';

type Props = {
  model: OrganizationOldVerifyPageModelType;
};

export type OrganizationOldVerifyFormValues = {
  ProofOfAffiliationType: UiRadioGroupPropsValue;
  ProofOfAffiliationWebsite: string | null;
  Files: {
    ID: number;
    Uri: string;
    Name: string;
  }[];
};

export const OrganizationOldVerifyComponent: FC<Props> = ({ model }) => {
  const locale = 'organization.oldVerify';

  const tHelper = useNsTHelper(locale);

  const Approved = model.StatusID === Enums.organizations.StatusCode.Approved;

  const InPendingVerification =
    model.StatusID === Enums.organizations.StatusCode.PendingVerification ||
    model.StatusID === Enums.organizations.StatusCode.Frozen;

  const formProps = useStateForm<OrganizationOldVerifyFormValues>({
    defaultValues: {
      ProofOfAffiliationType: model.ProofOfAffiliationType,
      ProofOfAffiliationWebsite: model.ProofOfAffiliationWebsite,
      Files: model.Files,
    },
  });

  useBeforeunloadOrganizationClean(formProps.getDirtyFields);

  const [ProofOfAffiliationType] = useStateFormValueWatch<OrganizationOldVerifyFormValues, ['ProofOfAffiliationType']>(
    formProps.getSubscribeProps,
    ['ProofOfAffiliationType']
  );

  const resetDirtyForm = () => {
    formProps.reset(formProps.getValue());
  };

  const { apiCaller, loading } = useOneRequest(apiOrganizationSaveVerifyAccount, {
    onSuccess: () => {
      UiToast.success(tHelper('savedSuccess'));

      resetDirtyForm();

      hardRedirect(ROUTE_START_GUIDE_ORGANIZATION);
    },
  });

  const onSubmit = () => {
    if (formProps.getValue('ProofOfAffiliationType') && !Approved) {
      const modelToSave: OrganizationOldVerifyPageSaveRequestType = {
        ...model,
        Files: formProps.getValue('Files'),
        ProofOfAffiliationType: formProps.getValue('ProofOfAffiliationType') as number,
        ProofOfAffiliationWebsite: formProps.getValue('ProofOfAffiliationWebsite'),
        IsDirty: true,
      };

      apiCaller(modelToSave);
    }
  };

  return (
    <OrganizationLayout
      header={tHelper('title')}
      title={model.Name}
      onSaveClick={onSubmit}
      isSaving={loading}
      saveTooltipText={ProofOfAffiliationType === null ? tHelper('selectOneVerificationMethod') : undefined}
      saveText={
        model.StatusID === Enums.organizations.StatusCode.PendingVerification ? tHelper('pendingSaveText') : undefined
      }
      cancelText={
        model.StatusID === Enums.organizations.StatusCode.PendingVerification ? tHelper('pendingCancelText') : undefined
      }
    >
      <div id="verifyAccountBindingContainer">
        <div id="wrapper" className="container">
          <section id="organization-setup-verify-account">
            <div className="container">
              <div className="row">
                <div className="col-sm-12">
                  {InPendingVerification && (
                    <OrganizationOldVerifyInPendingVerificationPartial model={model} locale={locale} />
                  )}
                </div>
              </div>

              {model.ShowOrganizationVerificationCard ? (
                <OrganizationOldVerifyVerificationCardPartial
                  formProps={formProps}
                  locale={locale}
                  model={model}
                  resetDirtyForm={resetDirtyForm}
                />
              ) : (
                <OrganizationOldVerifyNotVerificationCardPartial
                  formProps={formProps}
                  locale={locale}
                  model={model}
                  resetDirtyForm={resetDirtyForm}
                />
              )}
            </div>
          </section>
        </div>
      </div>
    </OrganizationLayout>
  );
};
